import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import StorageService from './services/storage/storageService.js';
import auth2FAService from './services/auth/auth2FAService';
import { setApiBaseUrlForCompany } from './utils/storage.js';
import LoginScreen from './screens/Login/index.jsx';
import LoadingSpinner from './components/LoadingSpinner.js';
import NotificationContainer from './components/Notifications/NotificationContainer.jsx';
import PrivateRoute from './components/Layout/PrivateRoute.jsx';
import useTokenExpirationMonitor from './hooks/useTokenExpirationMonitor.js';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext.jsx';
import { FEATURE_CODES } from './utils/features.js';
import { profileAPI } from './services/api/profileAPI.js';
import { languageStore } from './i18n/languageStore.js';
import { useLanguage } from './i18n/LanguageContext.jsx';
import { BILLING_ALLOWED_ROLES } from './utils/billingAccess.js';
import { DRIVER_ADMIN_ROLES } from './utils/driverAdministration.js';
import {
  LKW_INVOICE_ALLOWED_ROLES,
  LKW_INVOICE_REQUIRED_FEATURES,
} from './utils/lkwInvoiceAccess.js';
import { lazyScreen } from './utils/lazyScreen.js';

const ForgotPassword = lazyScreen(() => import('./screens/Auth/ForgotPassword.jsx'));
const ResetPasswordConfirm = lazyScreen(() => import('./screens/Auth/ResetPasswordConfirm.jsx'));
const TwoFactorAuth = lazyScreen(() => import('./screens/TwoFactorAuth/index.jsx'));
const SessionsScreen = lazyScreen(() => import('./screens/Sessions/index.jsx'));
const TwoFactorSettingsScreen = lazyScreen(() => import('./screens/TwoFactorSettings/index.jsx'));
const loadMainRouter = () => import('./screens/Main/MainRouter.jsx');
const MainRouter = lazyScreen(loadMainRouter);
const DriversScreen = lazyScreen(() => import('./screens/DriverScreen/index.jsx'));
const DriverTripSheetsScreen = lazyScreen(() => import('./screens/DriverTripSheets/index.jsx'));
const TransportsScreen = lazyScreen(() => import('./screens/Transports/index.jsx'));
const AddDriverScreen = lazyScreen(() => import('./screens/AddDriver/index.jsx'));
const CreateTransportScreen = lazyScreen(() => import('./screens/CreateTransport/index.jsx'));
const TruckScreen = lazyScreen(() => import('./screens/Trucks/index.jsx'));
const AddTruck = lazyScreen(() => import('./screens/AddTruck/index.jsx'));
const AddTrailer = lazyScreen(() => import('./screens/AddTrailer/index.jsx'));
const TrailerScreen = lazyScreen(() => import('./screens/Trailers/index.jsx'));
const ProfileScreen = lazyScreen(() => import('./screens/Profile/index.jsx'));
const RouteScreen = lazyScreen(() => import('./screens/Route/index.jsx'));
const CompleteCMR = lazyScreen(() => import('./screens/CompleteCMR/index.jsx'));
const CMRPreview = lazyScreen(() => import('./screens/ReviewCMR/index.jsx'));
const NotificationsPage = lazyScreen(() => import('./screens/Notifications/index.jsx'));
const ExpiredDocumentsScreen = lazyScreen(() => import('./screens/ExpiredDocuments/index.jsx'));
const Status = lazyScreen(() => import('./screens/Status/index.jsx'));
const ExpeditorScreen = lazyScreen(() => import('./screens/Expeditor/index.jsx'));
const TemporaryAccessScreen = lazyScreen(() => import('./screens/TemporaryAccess/index.jsx'));
const AdminUsersScreen = lazyScreen(() => import('./screens/AdminUsers/index.jsx'));
const CMRManagementScreen = lazyScreen(() => import('./screens/CMRManagement/index.jsx'));
const UITManagementScreen = lazyScreen(() => import('./screens/UITManagement/index.jsx'));
const ProfitabilityScreen = lazyScreen(() => import('./screens/Profitability/index.jsx'));
const FreightForwardingPartnersScreen = lazyScreen(() => import('./screens/FreightForwardingPartners/index.jsx'));
const FleetTrucksScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/FleetTrucksScreen.jsx'));
const FleetTruckDetailsScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/FleetTruckDetailsScreen.jsx'));
const FleetTruckEditScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/FleetTruckEditScreen.jsx'));
const FleetTrailersScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/FleetTrailersScreen.jsx'));
const LightVehiclesScreen = lazyScreen(() => import('./screens/FleetManager/LightVehicles/LightVehiclesScreen.jsx'));
const LightVehicleFormScreen = lazyScreen(() => import('./screens/FleetManager/LightVehicles/LightVehicleFormScreen.jsx'));
const TruckDocumentsSummaryScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/TruckDocumentsSummaryScreen.jsx'));
const TrailerDocumentsSummaryScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/TrailerDocumentsSummaryScreen.jsx'));
const VehicleDocumentsSummaryScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/VehicleDocumentsSummaryScreen.jsx'));
const VehicleDocumentsLifecycleScreen = lazyScreen(() => import('./screens/FleetManager/FleetVehicles/VehicleDocumentsLifecycleScreen.jsx'));
const LeaveHeatmapScreen = lazyScreen(() => import('./screens/LeaveHeatmap/index.jsx'));
const InterventionsListScreen = lazyScreen(() => import('./screens/FleetManager/Interventions/InterventionsListScreen.jsx'));
const MaintenanceDashboard = lazyScreen(() => import('./screens/FleetManager/Maintenance/MaintenanceDashboard.jsx'));
const CreateInterventionScreen = lazyScreen(() => import('./screens/FleetManager/Interventions/CreateInterventionScreen.jsx'));
const InterventionDetailsScreen = lazyScreen(() => import('./screens/FleetManager/Interventions/InterventionDetailsScreen.jsx'));
const VehicleHistoryScreen = lazyScreen(() => import('./screens/FleetManager/Interventions/VehicleHistoryScreen.jsx'));
const UpcomingInterventionsScreen = lazyScreen(() => import('./screens/FleetManager/Interventions/UpcomingInterventionsScreen.jsx'));
const TransportDocumentPreview = lazyScreen(() => import('./screens/Transports/TransportDocumentPreview.jsx'));
const DriverObservationsScreen = lazyScreen(() => import('./screens/DriverObservations/index.jsx'));
const OptifleetTachographScreen = lazyScreen(() => import('./screens/OptifleetTachograph/index.jsx'));
const OptifleetDriverDailyReport = lazyScreen(() => import('./screens/OptifleetDriverDailyReport/index.jsx'));
const TransportQuotationsScreen = lazyScreen(() => import('./screens/TransportQuotations/index.jsx'));
const BillingScreen = lazyScreen(() => import('./screens/Billing/index.jsx'));
const BillingEditor = lazyScreen(() => import('./screens/Billing/BillingEditor.jsx'));
const BillingDetail = lazyScreen(() => import('./screens/Billing/BillingDetail.jsx'));
const ReceivedInvoices = lazyScreen(() => import('./screens/Billing/ReceivedInvoices.jsx'));
const RecurrencesList = lazyScreen(() => import('./screens/Billing/RecurrencesList.jsx'));
const RecurrenceDetail = lazyScreen(() => import('./screens/Billing/RecurrenceDetail.jsx'));
const FreightForwardingBillingQueue = lazyScreen(() => import('./screens/FreightForwarding/BillingQueue.jsx'));
const FreightForwardingWorkflowPage = lazyScreen(() => import('./screens/FreightForwarding/FreightWorkflowPage.jsx'));
const PublicFreightUpload = lazyScreen(() => import('./screens/FreightForwarding/PublicFreightUpload.jsx'));

function AppContent() {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [userToken, setUserToken] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [tenantUnavailableStatus, setTenantUnavailableStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isTemporaryAccessRoute = location.pathname.startsWith('/temp/');
  const isFreightPublicUploadRoute = location.pathname === '/freight-upload';
  const isPublicPossessionRoute = isTemporaryAccessRoute || isFreightPublicUploadRoute;

  // Monitor token expiration with navigate from Router context
  const { formatTimeRemaining } = useTokenExpirationMonitor(
    userToken,
    () => setUserToken(null),
    navigate
  );

  // Check if user is logged in on app start and validate token expiry
  useEffect(() => {
    if (isPublicPossessionRoute) {
      // A share is possession-based public access. Do not bootstrap or attach
      // an unrelated browser session while this route is open.
      setUserToken(null);
      setUserProfile(null);
      setTenantUnavailableStatus(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    const bootstrapAsync = async () => {
      const applyProfile = (profile) => {
        if (cancelled) return;
        const synchronizedProfile = languageStore.applyUser(profile);
        StorageService.set('userData', synchronizedProfile);
        setUserProfile(synchronizedProfile);
        window.dispatchEvent(new CustomEvent('atrux:user-language', { detail: { user: synchronizedProfile } }));
        setApiBaseUrlForCompany(synchronizedProfile);
      };

      try {
        const token = StorageService.getAuthToken();
        if (token) {
          // Download the dashboard route while authentication/profile requests are in flight.
          loadMainRouter();
          // Check if token is expired
          const isValid = await auth2FAService.checkAndRefreshToken();
          if (cancelled) return;
          if (isValid) {
            const cachedProfile = StorageService.get('userData');
            if (cachedProfile) {
              applyProfile(cachedProfile);
              setUserToken(token);

              // Keep the fast cached render, then refresh profile details in the background.
              profileAPI.getProfile(token)
                .then(applyProfile)
                .catch((profileError) => {
                  if (!cancelled) console.error('Profile refresh failed:', profileError);
                });
            } else {
              try {
                const profile = await profileAPI.getProfile(token);
                applyProfile(profile);
                setUserToken(token);
              } catch (profileError) {
                if (!cancelled) {
                  console.error('Profile refresh failed:', profileError);
                  setUserToken(token);
                }
              }
            }
          } else {
            // Token expired - clear it
            StorageService.clearAuthData();
            setUserToken(null);
          }
        }
      } catch (e) {
        if (!cancelled) console.error('Auth check failed:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    bootstrapAsync();
    return () => {
      cancelled = true;
    };
  }, [isPublicPossessionRoute]);

  useEffect(() => {
    const handleAuthRevoked = (event) => {
      StorageService.clearAuthData();
      setUserToken(null);
      navigate('/login', {
        replace: true,
        state: {
          sessionMessage: event.detail?.message || t('app.sessionExpired'),
        },
      });
    };

    window.addEventListener('atrux:auth-revoked', handleAuthRevoked);
    return () => window.removeEventListener('atrux:auth-revoked', handleAuthRevoked);
  }, [navigate, t]);

  useEffect(() => {
    const handleTenantUnavailable = (event) => {
      setTenantUnavailableStatus(event.detail?.status || 404);
    };
    window.addEventListener('atrux:tenant-unavailable', handleTenantUnavailable);
    return () => window.removeEventListener('atrux:tenant-unavailable', handleTenantUnavailable);
  }, []);

  // Function to handle login
  const handleLogin = (token) => {
    setUserProfile(null);
    setUserToken(token);
    StorageService.setAuthToken(token);
  };

  // Function to handle logout with notification cleanup
  const handleLogout = () => {
    setUserProfile(null);
    setUserToken(null);
    StorageService.clearAuthData();
  };

  // Always reset page scroll when route changes.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const layoutScrollContainers = document.querySelectorAll('.dashboard-content, .dashboard-main, main');
    layoutScrollContainers.forEach((container) => {
      container.scrollTop = 0;
    });
  }, [location.pathname, location.search]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-100 via-white to-neutral-200 flex items-center justify-center">
        <LoadingSpinner showText={false} />
      </div>
    );
  }

  if (tenantUnavailableStatus) {
    const temporary = tenantUnavailableStatus === 503;
    return (
      <main className="min-h-screen bg-neutral-100 p-6 flex items-center justify-center">
        <section className="w-full max-w-lg rounded-3xl border border-neutral-200 bg-white p-8 text-center shadow-xl">
          <h1 className="text-2xl font-bold text-neutral-800">
            {temporary ? t('app.companyTemporarilyUnavailable') : t('app.companyUnavailable')}
          </h1>
          <p className="mt-3 text-neutral-600">
            {temporary
              ? t('app.retryLater')
              : t('app.checkAddress')}
          </p>
          {temporary && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 rounded-xl bg-primary px-5 py-3 font-semibold text-white"
            >
              {t('common.retry')}
            </button>
          )}
        </section>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <FeatureFlagsProvider authToken={userToken}>
      <Suspense fallback={<LoadingSpinner showText={false} />}>
      <Routes>
        <Route
          path="/login"
          element={
            userToken ? (
              <Navigate to="/main" replace />
            ) : (
              <LoginScreen onLogin={handleLogin} />
            )
          }
        />
        <Route
          path="/forgot-password"
          element={
            userToken ? (
              <Navigate to="/main" replace />
            ) : (
              <ForgotPassword />
            )
          }
        />
        <Route
          path="/password/reset/confirm/:uid/:token"
          element={<ResetPasswordConfirm />}
        />
        <Route
          path="/api/v0.1/password/reset/confirm/:uid/:token"
          element={<ResetPasswordConfirm />}
        />
        <Route
          path="/api/v0.1/password/reset/confirm/:uid/:token/"
          element={<ResetPasswordConfirm />}
        />
        <Route
          path="/2fa-verification"
          element={
            userToken ? (
              <Navigate to="/main" replace />
            ) : (
              <TwoFactorAuth />
            )
          }
        />
        <Route
          path="/expeditor"
          element={<ExpeditorScreen />}
        />
        <Route
          path="/temp/:code"
          element={<TemporaryAccessScreen />}
        />
        <Route
          path="/freight-upload"
          element={<PublicFreightUpload />}
        />
        <Route
          path="/main"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout}>
              <MainRouter onLogout={handleLogout} initialUserProfile={userProfile} />
            </PrivateRoute>
          }
        />
        <Route
          path="/drivers"
          element={
            <PrivateRoute
              userToken={userToken}
              onLogout={handleLogout}
              featureCode={FEATURE_CODES.WORKFORCE_MANAGEMENT}
              allowedRoles={DRIVER_ADMIN_ROLES}
            >
              <DriversScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/drivers/:driverId/trip-sheets"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.WORKFORCE_MANAGEMENT}>
              <DriverTripSheetsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/transports"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.TRANSPORT_MANAGEMENT}>
              <TransportsScreen/>
            </PrivateRoute>
          }
        />
        <Route
          path="/transports/:transportId"
          element={<Navigate to="/transports" replace />}
        />
        <Route
          path="/transports/*"
          element={<Navigate to="/transports" replace />}
        />
        <Route
          path="/addDriver"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.WORKFORCE_MANAGEMENT}>
              <AddDriverScreen/>
            </PrivateRoute>
          }
        />
        <Route
          path="/createTransport"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.TRANSPORT_MANAGEMENT}>
              <CreateTransportScreen/>
            </PrivateRoute>
          }
        />
        <Route
          path="/transport-quotations"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.TRANSPORT_QUOTATIONS}>
              <TransportQuotationsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/freight-forwarding/customers"
          element={
            <PrivateRoute
              userToken={userToken}
              onLogout={handleLogout}
              featureCodes={[FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.FREIGHT_FORWARDING]}
            >
              <FreightForwardingPartnersScreen role="customer" />
            </PrivateRoute>
          }
        />
        <Route
          path="/freight-forwarding/carriers"
          element={
            <PrivateRoute
              userToken={userToken}
              onLogout={handleLogout}
              featureCodes={[FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.FREIGHT_FORWARDING]}
            >
              <FreightForwardingPartnersScreen role="carrier" />
            </PrivateRoute>
          }
        />
        <Route
          path="/freight-forwarding/billing"
          element={
            <PrivateRoute
              userToken={userToken}
              onLogout={handleLogout}
              featureCodes={[FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.FREIGHT_FORWARDING]}
              allowedRoles={BILLING_ALLOWED_ROLES}
            >
              <FreightForwardingBillingQueue />
            </PrivateRoute>
          }
        />
        <Route
          path="/freight-forwarding/dosar/:transportId"
          element={
            <PrivateRoute
              userToken={userToken}
              onLogout={handleLogout}
              featureCodes={[FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.FREIGHT_FORWARDING]}
              allowedRoles={BILLING_ALLOWED_ROLES}
            >
              <FreightForwardingWorkflowPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/addTruck"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <AddTruck />
            </PrivateRoute>
          }
        />
        <Route
          path="/trucks"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <TruckScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/trucks/:id/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleDocumentsLifecycleScreen vehicleType="truck" backPath="/trucks" />
            </PrivateRoute>
          }
        />
        <Route
          path="/addTrailer"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <AddTrailer />
            </PrivateRoute>
          }
        />
        <Route
          path="/trailers"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <TrailerScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/trailers/:id/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleDocumentsLifecycleScreen vehicleType="trailer" backPath="/trailers" />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout}>
              <ProfileScreen onLogout={handleLogout} />
            </PrivateRoute>
          }
        />
        <Route
          path="/sessions"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout}>
              <SessionsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/2fa-settings"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout}>
              <TwoFactorSettingsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/status/:transportId"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.TRANSPORT_MANAGEMENT}>
              <Status />
            </PrivateRoute>
          }
        />
        <Route
          path="/route"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.ROUTE_PLANNING}>
              <RouteScreen authToken={userToken} />
            </PrivateRoute>
          }
        />
        <Route path="/live-tracking" element={<Navigate to="/main" replace />} />
        <Route
          path="/complete-cmr/:transportId"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCodes={[FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.ECMR]}>
              <CompleteCMR />
            </PrivateRoute>
          }
        />
        <Route path="/review-cmr/:transportId"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCodes={[FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.ECMR]}>
              <CMRPreview />
            </PrivateRoute>
          }
        />
        <Route path="/notifications"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout}>
              <NotificationsPage />
            </PrivateRoute>
          }
        />
        <Route path="/expired-documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout}>
              <ExpiredDocumentsScreen />
            </PrivateRoute>
          }
        />
        <Route path="/admin/users"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.ADMIN_TOOLS}>
              <AdminUsersScreen />
            </PrivateRoute>
          }
        />
        <Route path="/admin/activity-logs" element={<Navigate to="/main" replace />} />
        <Route path="/cmr-management"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCodes={[FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.ECMR]}>
              <CMRManagementScreen />
            </PrivateRoute>
          }
        />
        <Route path="/uit"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.ETRANSPORT}>
              <UITManagementScreen />
            </PrivateRoute>
          }
        />
        <Route path="/billing"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.EFACTURA} allowedRoles={BILLING_ALLOWED_ROLES}>
              <BillingScreen />
            </PrivateRoute>
          }
        />
        <Route path="/billing/new"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.EFACTURA} allowedRoles={BILLING_ALLOWED_ROLES}>
              <BillingEditor />
            </PrivateRoute>
          }
        />
        <Route path="/billing/received"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.EFACTURA} allowedRoles={BILLING_ALLOWED_ROLES}>
              <ReceivedInvoices />
            </PrivateRoute>
          }
        />
        <Route path="/billing/recurrences"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.EFACTURA} allowedRoles={BILLING_ALLOWED_ROLES}>
              <RecurrencesList />
            </PrivateRoute>
          }
        />
        <Route path="/billing/recurrences/:recurrenceId"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.EFACTURA} allowedRoles={BILLING_ALLOWED_ROLES}>
              <RecurrenceDetail />
            </PrivateRoute>
          }
        />
        <Route path="/billing/:invoiceId/edit"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.EFACTURA} allowedRoles={BILLING_ALLOWED_ROLES}>
              <BillingEditor />
            </PrivateRoute>
          }
        />
        <Route path="/billing/:invoiceId"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.EFACTURA} allowedRoles={BILLING_ALLOWED_ROLES}>
              <BillingDetail />
            </PrivateRoute>
          }
        />
        <Route path="/profitability/invoice-generator"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCodes={LKW_INVOICE_REQUIRED_FEATURES} allowedRoles={LKW_INVOICE_ALLOWED_ROLES}>
              <ProfitabilityScreen />
            </PrivateRoute>
          }
        />
        <Route path="/profitability/invoice-generator/draft"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCodes={LKW_INVOICE_REQUIRED_FEATURES} allowedRoles={LKW_INVOICE_ALLOWED_ROLES}>
              <ProfitabilityScreen />
            </PrivateRoute>
          }
        />
        <Route path="/profitability/*"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.PROFITABILITY}>
              <ProfitabilityScreen />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/trucks/add"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <AddTruck onNavigateBack={() => navigate('/fleet/trucks')} />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trucks/:id/edit"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <FleetTruckEditScreen />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trucks/:id/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleDocumentsLifecycleScreen vehicleType="truck" />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trucks/:id"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <FleetTruckDetailsScreen />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/vehicle-documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleDocumentsSummaryScreen />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trucks/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <TruckDocumentsSummaryScreen />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trucks"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <FleetTrucksScreen />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trailers"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <FleetTrailersScreen />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trailers/:id/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleDocumentsLifecycleScreen vehicleType="trailer" />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/trailers/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <TrailerDocumentsSummaryScreen />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/cars"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <LightVehiclesScreen vehicleType="passenger_car" />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/cars/add"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <LightVehicleFormScreen vehicleType="passenger_car" />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/cars/:id/edit"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <LightVehicleFormScreen vehicleType="passenger_car" />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/cars/:id/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleDocumentsLifecycleScreen vehicleType="passenger_car" backPath="/fleet/cars" />
            </PrivateRoute>
          }
        />

        <Route path="/fleet/vans"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <LightVehiclesScreen vehicleType="van" />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/vans/add"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <LightVehicleFormScreen vehicleType="van" />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/vans/:id/edit"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <LightVehicleFormScreen vehicleType="van" />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/vans/:id/documents"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleDocumentsLifecycleScreen vehicleType="van" backPath="/fleet/vans" />
            </PrivateRoute>
          }
        />

        {/* Maintenance and Intervention Routes */}
        <Route path="/fleet/maintenance"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT} allowedRoles={['admin', 'fleet_manager', 'dispatcher']}>
              <MaintenanceDashboard />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/interventions"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <InterventionsListScreen />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/interventions/create"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <CreateInterventionScreen />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/interventions/upcoming"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <UpcomingInterventionsScreen />
            </PrivateRoute>
          }
        />
        <Route path="/transport-documents/preview"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.TRANSPORT_MANAGEMENT}>
              <TransportDocumentPreview />
            </PrivateRoute>
          }
        />
        <Route path="/leave-heatmap"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.WORKFORCE_MANAGEMENT}>
              <LeaveHeatmapScreen />
            </PrivateRoute>
          }
        />
        <Route path="/driver-observations"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.WORKFORCE_MANAGEMENT}>
              <DriverObservationsScreen />
            </PrivateRoute>
          }
        />
        <Route path="/cargotrack-tachograph/*" element={<Navigate to="/main" replace />} />
        <Route path="/optifleet-tachograph"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.OPTIFLEET}>
              <OptifleetTachographScreen />
            </PrivateRoute>
          }
        />
        <Route path="/driver-daily-report/*" element={<Navigate to="/main" replace />} />
        <Route path="/optifleet-driver-daily-report/:driverId"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.OPTIFLEET}>
              <OptifleetDriverDailyReport />
            </PrivateRoute>
          }
        />
        <Route path="/trip-planning/*" element={<Navigate to="/main" replace />} />
        <Route path="/fleet/interventions/:id"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <InterventionDetailsScreen />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/interventions/:id/edit"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <CreateInterventionScreen />
            </PrivateRoute>
          }
        />
        <Route path="/fleet/vehicle/:vehicleType/:vehicleId/history"
          element={
            <PrivateRoute userToken={userToken} onLogout={handleLogout} featureCode={FEATURE_CODES.FLEET_MANAGEMENT}>
              <VehicleHistoryScreen />
            </PrivateRoute>
          }
        />

        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
      </FeatureFlagsProvider>

      {/* Notification and floating components */}
      <NotificationContainer />
    </div>
  );
}

export default AppContent;
